import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import AdBudgetFields from "./AdBudgetFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENDERS = [
  { key: "male", label: "Masculino" },
  { key: "female", label: "Feminino" },
  { key: "other", label: "Outro" },
];

export const EMPTY_AD_FORM = {
  title: "", description: "", image_url: "", video_url: "", target_url: "", cta: "Saiba mais",
  target_cities: "", target_states: "", target_countries: "", target_genders: [], target_interests: "",
  budget_type: "none", daily_budget: "", total_budget: "", duration_days: "",
};

export const adToForm = (a) => ({
  ...EMPTY_AD_FORM,
  title: a.title || "", description: a.description || "", image_url: a.image_url || "", video_url: a.video_url || "",
  target_url: a.target_url || "", cta: a.cta || "Saiba mais",
  target_cities: (a.target_cities || []).join(", "), target_states: (a.target_states || []).join(", "), target_countries: (a.target_countries || []).join(", "), target_genders: a.target_genders || [], target_interests: (a.target_interests || []).join(", "),
  budget_type: a.budget_type || "none", daily_budget: a.daily_budget ?? "", total_budget: a.total_budget ?? "", duration_days: a.duration_days ?? "",
});

const toPayload = (form) => ({
  title: form.title, description: form.description, image_url: form.image_url || null, video_url: form.video_url || null,
  target_url: form.target_url || null, cta: form.cta,
  target_cities: form.target_cities.split(",").map((s) => s.trim()).filter(Boolean),
  target_states: form.target_states.split(",").map((s) => s.trim()).filter(Boolean),
  target_countries: form.target_countries.split(",").map((s) => s.trim()).filter(Boolean),
  target_genders: form.target_genders,
  target_interests: form.target_interests.split(",").map((s) => s.trim()).filter(Boolean),
  budget_type: form.budget_type,
  daily_budget: form.budget_type === "daily" ? Number(form.daily_budget || 0) : null,
  total_budget: form.budget_type === "total" ? Number(form.total_budget || 0) : null,
  duration_days: form.budget_type !== "none" && form.duration_days ? Number(form.duration_days) : null,
});

/** Create (editing=null) or edit (editing=ad) dialog for a simple ad: text, image OR video, link, targeting, budget. */
export default function AdFormDialog({ open, onOpenChange, editing, isAdmin, cpm, walletBalance, onSaved }) {
  const [form, setForm] = useState(EMPTY_AD_FORM);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(editing ? adToForm(editing) : EMPTY_AD_FORM); }, [open, editing]);

  const toggleGender = (g) => setForm((f) => ({ ...f, target_genders: f.target_genders.includes(g) ? f.target_genders.filter((x) => x !== g) : [...f.target_genders, g] }));

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Preencha título e descrição");
    if (form.budget_type === "daily" && Number(form.daily_budget) < 5) return toast.error("Orçamento diário mínimo: R$ 5,00");
    if (form.budget_type === "total" && (Number(form.total_budget) < 10 || !Number(form.duration_days))) return toast.error("Informe orçamento total (mín. R$ 10) e duração em dias");
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/ads/${editing.ad_id}`, toPayload(form));
        toast.success("Anúncio atualizado!");
      } else {
        await axios.post(`${API}/ads`, toPayload(form));
        toast.success(isAdmin ? "Anúncio publicado!" : "Anúncio enviado para moderação!");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar anúncio");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ad-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar anúncio" : "Novo anúncio"}</DialogTitle>
          <DialogDescription>Texto, foto ou vídeo, link, segmentação e orçamento do anúncio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="ad-title" placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea data-testid="ad-description" placeholder="Descrição" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          <div className="space-y-2" data-testid="ad-media-block">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Mídia (foto ou vídeo)</div>
            <div className="flex gap-2 flex-wrap">
              <ImageUploader testid="ad-upload-btn" size="default" label="Foto" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url, video_url: "" }))} />
              <ImageUploader testid="ad-upload-video-btn" size="default" label="Vídeo" accept="video/*" onUploaded={(url) => setForm((f) => ({ ...f, video_url: url, image_url: "" }))} />
              <Input data-testid="ad-image" placeholder="ou cole a URL da imagem/vídeo" className="flex-1 min-w-[180px]" value={form.video_url || form.image_url}
                onChange={(e) => { const v = e.target.value; const isVid = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(v); setForm({ ...form, image_url: isVid ? "" : v, video_url: isVid ? v : "" }); }} />
            </div>
            {form.video_url && (
              <div className="relative" data-testid="ad-video-preview">
                <video src={form.video_url} className="rounded-lg max-h-44 w-full object-cover bg-black" controls muted playsInline />
                <button type="button" onClick={() => setForm({ ...form, video_url: "" })} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Remover vídeo" data-testid="ad-video-remove"><X className="w-4 h-4" /></button>
              </div>
            )}
            {form.image_url && !form.video_url && (
              <div className="relative inline-block" data-testid="ad-image-preview">
                <img src={form.image_url} alt="preview" className="rounded-lg max-h-32 object-cover" />
                <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Remover imagem" data-testid="ad-image-remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>

          <Input data-testid="ad-target-url" placeholder="Link (https://...)" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} />
          <Input data-testid="ad-cta" placeholder="Botão (ex: Saiba mais, Comprar agora)" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} />

          <div className="pt-3 border-t border-border space-y-2" data-testid="ad-targeting-block">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Segmentação (opcional)</div>
            <p className="text-[11px] text-muted-foreground">Deixe em branco para exibir para todos os usuários.</p>
            <Input data-testid="ad-target-countries" placeholder="Países (separados por vírgula, ex: Brasil, Portugal)" value={form.target_countries} onChange={(e) => setForm({ ...form, target_countries: e.target.value })} />
            <Input data-testid="ad-target-states" placeholder="Estados (ex: São Paulo, SP, Minas Gerais)" value={form.target_states} onChange={(e) => setForm({ ...form, target_states: e.target.value })} />
            <Input data-testid="ad-target-cities" placeholder="Cidades (separadas por vírgula, ex: São Paulo, Rio de Janeiro)" value={form.target_cities} onChange={(e) => setForm({ ...form, target_cities: e.target.value })} />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Gênero:</span>
              {GENDERS.map((g) => (
                <button key={g.key} type="button" onClick={() => toggleGender(g.key)} data-testid={`ad-target-gender-${g.key}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.target_genders.includes(g.key) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                  {g.label}
                </button>
              ))}
            </div>
            <Input data-testid="ad-target-interests" placeholder="Interesses (ex: tecnologia, moda, esportes)" value={form.target_interests} onChange={(e) => setForm({ ...form, target_interests: e.target.value })} />
          </div>

          <AdBudgetFields value={form} onChange={setForm} cpm={cpm} walletBalance={walletBalance} />

          {!editing && !isAdmin && (
            <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
              Todo novo anúncio passa pela moderação da equipe antes de ser exibido publicamente.
            </div>
          )}
          <Button data-testid="ad-submit" onClick={save} disabled={saving} className="w-full rounded-full">{saving ? "Salvando..." : editing ? "Salvar alterações" : "Publicar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
